import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calculator, Leaf, Mail, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface EmailCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (email: string) => void;
  type: "budget" | "carbon";
  isLoading?: boolean;
}

const FREE_EMAIL_DOMAINS = [
  "gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "live.com",
  "aol.com", "icloud.com", "mail.com", "protonmail.com", "yandex.com",
  "gmx.com", "zoho.com", "free.fr", "orange.fr", "wanadoo.fr", "laposte.net"
];

const EmailCaptureModal = ({ isOpen, onClose, onSubmit, type, isLoading = false }: EmailCaptureModalProps) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Veuillez entrer une adresse email valide");
      return false;
    }
    
    const domain = email.split("@")[1]?.toLowerCase();
    if (FREE_EMAIL_DOMAINS.includes(domain)) {
      setError("Veuillez utiliser votre email professionnel");
      return false;
    }
    
    setError("");
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateEmail(email)) {
      onSubmit(email);
    }
  };

  const config = {
    budget: {
      icon: Calculator,
      title: "Découvrez votre budget",
      subtitle: "Entrez votre email professionnel pour voir l'analyse détaillée",
      buttonText: "Voir mon budget",
      iconBg: "bg-[#C6E278]",
      iconColor: "text-black",
      buttonClass: "bg-cta-green hover:bg-cta-green/90"
    },
    carbon: {
      icon: Leaf,
      title: "Découvrez votre impact",
      subtitle: "Entrez votre email professionnel pour voir l'analyse détaillée",
      buttonText: "Voir mon impact",
      iconBg: "bg-[#C6E278]",
      iconColor: "text-black",
      buttonClass: "bg-cta-green hover:bg-cta-green/90"
    }
  };

  const { icon: Icon, title, subtitle, buttonText, iconBg, iconColor, buttonClass } = config[type];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 flex items-center justify-center z-10"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-2xl p-8 shadow-xl max-w-sm w-full mx-4 relative"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center mb-6">
              <div className={`w-16 h-16 ${iconBg} rounded-full flex items-center justify-center mx-auto mb-4`}>
                <Icon className={`w-8 h-8 ${iconColor}`} />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">{title}</h3>
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-normal">
                  Email professionnel
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="votre@entreprise.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError("");
                    }}
                    className="pl-10"
                    disabled={isLoading}
                  />
                </div>
                {error && (
                  <p className="text-sm text-red-500">{error}</p>
                )}
              </div>

              <Button
                type="submit"
                className={`w-full ${buttonClass} text-black`}
                disabled={isLoading}
              >
                {isLoading ? "Chargement..." : buttonText}
              </Button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EmailCaptureModal;
